import { useState, useEffect } from 'react'
import { ItemAdvancedFormValues, ADR_CLASS_OPTIONS, getADRClassOptions } from '../../lib/itemFormHelpers'
import { opt } from '../../lib/optimizationStyles'

interface ItemAdvancedFieldsProps {
  values: ItemAdvancedFormValues
  onChange: (values: ItemAdvancedFormValues) => void
  idPrefix?: string
}

// ── Constantes types de palettes ─────────────────────────────────────────────

const PALLET_TYPE_OPTIONS = [
  {
    value: 'EUR',
    label: 'Europe EUR',
    subtitle: '1200×800 mm — 1500 kg max',
    color: '#16a34a',
  },
  {
    value: 'EUR2',
    label: 'Industrie EUR2',
    subtitle: '1200×1000 mm — 2000 kg max',
    color: '#0284c7',
  },
  {
    value: 'CUSTOM',
    label: 'Personnalisée',
    subtitle: 'Dimensions libres',
    color: '#7c3aed',
  },
] as const

// ─────────────────────────────────────────────────────────────────────────────

export default function ItemAdvancedFields({
  values,
  onChange,
  idPrefix = 'item',
}: ItemAdvancedFieldsProps) {
  const [adrOptions, setAdrOptions] = useState<{ value: string; label: string }[]>(
    ADR_CLASS_OPTIONS as any
  )

  useEffect(() => {
    let isMounted = true
    getADRClassOptions()
      .then((options) => {
        if (isMounted && options && options.length > 0) {
          setAdrOptions(options)
        }
      })
      .catch((err) => {
        console.warn('Failed to load ADR options in ItemAdvancedFields:', err)
      })
    return () => {
      isMounted = false
    }
  }, [])

  const set = (patch: Partial<ItemAdvancedFormValues>) => onChange({ ...values, ...patch })

  return (
    <fieldset className="space-y-4" aria-label="Contraintes avancées de l'article">
      <legend className={`text-sm font-semibold ${opt.dd}`}>Contraintes avancées</legend>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="flex items-center gap-2">
          <input
            id={`${idPrefix}-dangerous-goods`}
            type="checkbox"
            checked={values.dangerous_goods}
            onChange={(e) =>
              set({
                dangerous_goods: e.target.checked,
                ...(e.target.checked ? {} : { adr_class: '', un_number: '' }),
              })
            }
            className="w-4 h-4 text-amber-600 rounded"
          />
          <label htmlFor={`${idPrefix}-dangerous-goods`} className={`text-sm font-medium ${opt.dt}`}>
            Marchandise dangereuse (ADR)
          </label>
        </div>
      </div>

      {values.dangerous_goods && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-900/20">
          <div>
            <label htmlFor={`${idPrefix}-adr-class`} className={opt.label}>
              Classe ADR
            </label>
            <select
              id={`${idPrefix}-adr-class`}
              value={values.adr_class}
              onChange={(e) => set({ adr_class: e.target.value })}
              className={opt.input}
              aria-required="true"
            >
              <option value="">Sélectionner une classe</option>
              {adrOptions.map(({ value, label }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
              {values.adr_class && !adrOptions.some((o) => o.value === values.adr_class) && (
                <option value={values.adr_class}>
                  {values.adr_class}
                </option>
              )}
            </select>
          </div>
          <div>
            <label htmlFor={`${idPrefix}-un-number`} className={opt.label}>
              Numéro UN
            </label>
            <input
              id={`${idPrefix}-un-number`}
              type="text"
              placeholder="ex: UN1203"
              value={values.un_number}
              onChange={(e) => set({ un_number: e.target.value })}
              className={opt.input}
              aria-required="true"
            />
          </div>
        </div>
      )}

      {/* ── Section Palettisation ──────────────────────────────────────────────── */}
      <div className="space-y-3">
        {/* Checkbox principale */}
        <div className="flex items-center gap-2">
          <input
            id={`${idPrefix}-requires-pallet`}
            type="checkbox"
            checked={values.requires_pallet}
            onChange={(e) =>
              set({
                requires_pallet: e.target.checked,
                ...(!e.target.checked ? { pallet_type: 'EUR' } : {}),
              })
            }
            className="w-4 h-4 text-emerald-600 rounded"
          />
          <label
            htmlFor={`${idPrefix}-requires-pallet`}
            className={`text-sm font-medium ${opt.dt}`}
          >
            Palettiser automatiquement
          </label>
        </div>

        {/* Panneau options palette — visible seulement si requires_pallet */}
        {values.requires_pallet && (
          <div className="p-4 rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-900/20 space-y-4">

            {/* Type de palette — radio cards */}
            <div>
              <p className={`${opt.label} mb-2`}>Type de palette</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {PALLET_TYPE_OPTIONS.map(({ value, label, subtitle, color }) => {
                  const isSelected = values.pallet_type === value
                  return (
                    <label
                      key={value}
                      htmlFor={`${idPrefix}-pallet-type-${value}`}
                      className="cursor-pointer"
                    >
                      <input
                        id={`${idPrefix}-pallet-type-${value}`}
                        type="radio"
                        name={`${idPrefix}-pallet-type`}
                        value={value}
                        checked={isSelected}
                        onChange={() => set({ pallet_type: value })}
                        className="sr-only"
                      />
                      <div
                        className="rounded-lg border-2 p-3 transition-all"
                        style={{
                          borderColor: isSelected ? color : 'transparent',
                          background: isSelected
                            ? `${color}14`
                            : 'rgba(0,0,0,0.03)',
                          outline: isSelected
                            ? `1px solid ${color}40`
                            : undefined,
                        }}
                      >
                        <div
                          className="text-xs font-bold leading-tight"
                          style={{ color: isSelected ? color : undefined }}
                        >
                          {label}
                        </div>
                        <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5 leading-tight">
                          {subtitle}
                        </div>
                      </div>
                    </label>
                  )
                })}
              </div>
            </div>

            {/* Dimensions personnalisées — seulement si CUSTOM */}
            {values.pallet_type === 'CUSTOM' && (
              <div className="grid grid-cols-2 gap-3 p-3 rounded-md border border-violet-200 dark:border-violet-800 bg-violet-50/40 dark:bg-violet-900/20">
                <div>
                  <label htmlFor={`${idPrefix}-pallet-custom-length`} className={opt.label}>
                    Longueur (mm)
                  </label>
                  <input
                    id={`${idPrefix}-pallet-custom-length`}
                    type="number"
                    min={1}
                    placeholder="ex: 1200"
                    value={values.pallet_custom_length}
                    onChange={(e) => set({ pallet_custom_length: e.target.value })}
                    className={opt.input}
                    aria-required="true"
                  />
                </div>
                <div>
                  <label htmlFor={`${idPrefix}-pallet-custom-width`} className={opt.label}>
                    Largeur (mm)
                  </label>
                  <input
                    id={`${idPrefix}-pallet-custom-width`}
                    type="number"
                    min={1}
                    placeholder="ex: 800"
                    value={values.pallet_custom_width}
                    onChange={(e) => set({ pallet_custom_width: e.target.value })}
                    className={opt.input}
                    aria-required="true"
                  />
                </div>
              </div>
            )}

            {/* Hauteur max palette */}
            <div>
              <label htmlFor={`${idPrefix}-max-pallet-height`} className={opt.label}>
                Hauteur max palette (mm)
              </label>
              <input
                id={`${idPrefix}-max-pallet-height`}
                type="number"
                min={1}
                placeholder="ex: 1800 — défaut: hauteur du véhicule"
                value={values.max_pallet_height_mm}
                onChange={(e) => set({ max_pallet_height_mm: e.target.value })}
                className={opt.input}
              />
              <p className={opt.textHelp}>
                Limite imposée par le client, l'entrepôt ou le filmage. Si vide : utilise la hauteur du véhicule.
              </p>
            </div>

            {/* Hauteur plateau palette */}
            <div>
              <label htmlFor={`${idPrefix}-pallet-board-height`} className={opt.label}>
                Hauteur plateau palette (mm)
              </label>
              <input
                id={`${idPrefix}-pallet-board-height`}
                type="number"
                min={1}
                step="1"
                placeholder="ex: 150"
                value={values.pallet_board_height_mm}
                onChange={(e) => set({ pallet_board_height_mm: e.target.value })}
                className={opt.input}
              />
              <p className={opt.textHelp}>
                Par défaut: 150mm (standard EUR). Laisser vide pour utiliser la valeur par défaut.
              </p>
            </div>

            {/* Poids palette vide */}
            <div>
              <label htmlFor={`${idPrefix}-pallet-empty-weight`} className={opt.label}>
                Poids palette vide (kg)
              </label>
              <input
                id={`${idPrefix}-pallet-empty-weight`}
                type="number"
                min="0"
                step="0.1"
                placeholder="ex: 25"
                value={values.pallet_empty_weight_kg}
                onChange={(e) => set({ pallet_empty_weight_kg: e.target.value })}
                className={opt.input}
              />
              <p className={opt.textHelp}>
                Par défaut: EUR=25kg, EUR2=30kg, CUSTOM=20kg. Laisser vide pour utiliser la valeur par défaut.
              </p>
            </div>

            {/* Couleur palette */}
            <div>
              <label htmlFor={`${idPrefix}-pallet-color`} className={opt.label}>
                Couleur palette
              </label>
              <div className="flex items-center gap-3">
                <input
                  id={`${idPrefix}-pallet-color`}
                  type="color"
                  value={values.pallet_color || '#8B5E3C'}
                  onChange={(e) => set({ pallet_color: e.target.value })}
                  className="w-10 h-10 rounded cursor-pointer border-2 border-gray-300"
                />
                <input
                  type="text"
                  value={values.pallet_color || '#8B5E3C'}
                  onChange={(e) => set({ pallet_color: e.target.value })}
                  placeholder="#8B5E3C"
                  className={opt.input}
                  maxLength={7}
                />
              </div>
              <p className={opt.textHelp}>
                Couleur de la palette vide. Par défaut: #8B5E3C (bois marron). Format: #RRGGBB
              </p>
            </div>

            {/* Poids max palette chargée */}
            <div>
              <label htmlFor={`${idPrefix}-max-pallet-weight`} className={opt.label}>
                Poids max palette chargée (kg)
              </label>
              <input
                id={`${idPrefix}-max-pallet-weight`}
                type="number"
                min="0"
                step="0.1"
                placeholder="ex: 1500"
                value={values.max_pallet_weight_kg}
                onChange={(e) => set({ max_pallet_weight_kg: e.target.value })}
                className={opt.input}
              />
              <p className={opt.textHelp}>
                Par défaut: EUR=1500kg, EUR2=2000kg, CUSTOM=1000kg. Laisser vide pour utiliser la valeur par défaut.
              </p>
            </div>

            {/* Nb max colis par palette */}
            <div>
              <label htmlFor={`${idPrefix}-max-items-per-pallet`} className={opt.label}>
                Colis max par palette
              </label>
              <input
                id={`${idPrefix}-max-items-per-pallet`}
                type="number"
                min={0}
                placeholder="0 = calculé automatiquement"
                value={values.max_items_per_pallet}
                onChange={(e) => set({ max_items_per_pallet: e.target.value })}
                className={opt.input}
              />
              <p className={opt.textHelp}>
                0 = le moteur calcule automatiquement selon les dimensions.
              </p>
            </div>

          </div>
        )}
      </div>
      {/* ──────────────────────────────────────────────────────────────────────── */}


    </fieldset>
  )
}
